import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { ApiService } from 'src/app/api.service';
import { Helpers } from 'src/app/app.helpers';
import { RxStompService } from '../../rx-stomp.service';
import { OrganisationService } from 'src/app/organisation.service';
import { Message } from '@stomp/stompjs';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatProgressBarModule, MatProgressBar } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, MatSortHeader } from '@angular/material/sort';
import { MatInputModule, MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';
import { DatePipe } from '@angular/common';


@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, NgbTooltipModule, MatFormField, MatLabel, MatInput, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatProgressBar, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, DatePipe]
})
export class JobsComponent implements OnInit {
  fetch_type: number;
  jobs = [];
  hasApiError = false;
  apiError: string;
  inContextOrgId: string;
  // @ts-ignore, to suppress warning related to being undefined
  private statusSubscriptions: Array<Subscription>;
  // @ts-ignore, to suppress warning related to being undefined
  private progressSubscriptions: Array<Subscription>;
  dataSource = new MatTableDataSource<any>(this.jobs);
  displayedColumns: string[] = [
    'id',
    'creation_date',
    'initiator',
    'org',
    'title',
    'type',
    'status',
    'cpu_seconds',
    'percentage_complete',
    'node_id',
    'actions'];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public auth: AuthService,
    private api: ApiService,
    private orgService: OrganisationService,
    private rxStompService: RxStompService,
    private configFactory: AuthClientConfig,
    private route: ActivatedRoute,
    private router: Router,
    public helpers: Helpers

  ) { }

  ngOnInit(): void {

    // Get the fetch type from the route as a number (Default 10: Current jobs)
    this.fetch_type = parseInt(this.route.snapshot.paramMap.get('fetch_type') || '10');

    this.orgService.currentOrg$.subscribe((org) => {
      this.inContextOrgId = (org && org.id) ? org.id : '';
    });

    // If we're authenticated
    this.auth.isAuthenticated$.subscribe((res) => {
      if (res) {
        console.log("******Getting jobs ...");
        this.getJobs();
      }
    });
  }

  getJobs() {
    this.unsubscribeFromUpdates();

    this.api.getJobs$(this.inContextOrgId, this.fetch_type, 0).subscribe({
      next: (res) => {
        this.hasApiError = false;

        // Copy response this.jobs
        this.jobs = res;
        this.dataSource = new MatTableDataSource<any>(this.jobs);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        if (!this.statusSubscriptions) {
          this.statusSubscriptions = [];
        }

        if (!this.progressSubscriptions) {
          this.progressSubscriptions = [];
        }

        for (const job of this.jobs) {

          // Don't watch completed jobs
          if (job.status == 70) {
            continue;
          }

          // Subscribe to status updates
          this.statusSubscriptions.push(this.rxStompService
            .watch(`/topic/${job.id}.status`)
            .subscribe((msg: Message) => {
              var destination = msg.headers.destination;
              // Get job id from destination between last / and .status
              var jobId = destination.substring(
                destination.lastIndexOf('/') + 1,
                destination.lastIndexOf('.'));

              // Pull this job
              this.api.getJobsId$(jobId).subscribe({
                next: (res) => {
                  var job = this.jobs.find((j) => j.id == jobId);
                  if (job) {
                    job.status = res.status;
                    job.node_id = res.node_id;
                    job.cpu_seconds = res.cpu_seconds;
                    job.wall_time = res.wall_time;

                    if (job.status >= 70 /* completed/quarantined */) {
                      job.percentage_complete = 100;
                    }
                    this.jobs = [...this.jobs];
                    this.dataSource = new MatTableDataSource<any>(this.jobs);
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                  }
                },
                error: (error) => {
                  this.hasApiError = true;
                  this.apiError = error.message;
                },
              });
            }));

          // Subscribe to progress updates
          this.progressSubscriptions.push(this.rxStompService
            .watch(`/topic/${job.id}.*.*.progress`)
            .subscribe((msg: Message) => {
              var json = msg.body.substring(msg.body.indexOf('{'), msg.body.lastIndexOf('}') + 1);

              var progress = JSON.parse(json);
              if (progress.done) {
                // Calculate progress percentage
                var percentage = Math.round((progress.done / progress.total) * 100);

                // Update the job with the new progress
                var job = this.jobs.find((j) => j.id == progress.job_id);
                if (job) {
                  job.percentage_complete = percentage;
                  this.jobs = [...this.jobs];
                  this.dataSource = new MatTableDataSource<any>(this.jobs);
                  this.dataSource.sort = this.sort;
                  this.dataSource.paginator = this.paginator;
                }
              }
            }));
        }
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });
  }

  deleteJob(id: string, delete_type: number) {

    // Of we're requesting a hard delete
    if (delete_type == 10) {
      var msg = "This will permanently delete the job and all associated data. Are you sure?";
      var doDelete = confirm(msg);
      if (!doDelete) {
        return;
      }
    }

    this.api.deleteJobsId$(id, delete_type).subscribe({
      next: () => {
        this.hasApiError = false;
        this.getJobs()
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });
  }

  restoreJob(id: string) {
    this.api.deleteJobsId$(id, 20).subscribe({
      next: () => {
        this.hasApiError = false;
        this.router.navigateByUrl('/geometry/' + id);
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });
  }

  copyJob(id: string): void {
    this.hasApiError = false;

    this.api.getJobsId$(id).subscribe({
      next: (originalJob) => {

        this.api.copyJobs$(this.inContextOrgId, originalJob).subscribe({
          next: (newJob) => {
            this.unsubscribeFromUpdates();
            this.router.navigateByUrl('/geometry/' + newJob.id);
          },
          error: (error) => {
            this.hasApiError = true;
            this.apiError = error.message;
          },
        });
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });


  }

  queueJob(id: string) {
    this.api.putJobsIdQueue$(id).subscribe({
      next: () => {
        this.hasApiError = false;
        this.getJobs()
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });
  }

  ngOnDestroy() {
    this.unsubscribeFromUpdates();
  }

  unsubscribeFromUpdates() {

    if (this.statusSubscriptions) {
      console.log("Unsubscribing from status updates ...");
      for (const subscription of this.statusSubscriptions) {
        if (subscription) {
          subscription.unsubscribe();
        }
      }
      this.statusSubscriptions = [];
    }

    if (this.progressSubscriptions) {
      console.log("Unsubscribing from progress updates ...");
      for (const subscription of this.progressSubscriptions) {
        if (subscription) {
          subscription.unsubscribe();
        }
      }
      this.progressSubscriptions = [];
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
}
